// src/pages/customers/[id]/tabs/InvoiceList.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import API_BASE_URL from "../../../../utils/config";

function getCurrentFYRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-based
  const fyStartYear = m >= 3 ? y : y - 1; // Apr (3) or later -> same year else previous
  const start = new Date(fyStartYear, 3, 1);       // Apr 1
  const end = new Date(fyStartYear + 1, 2, 31);    // Mar 31 next year
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  return { from: toYMD(start), to: toYMD(end) };
}

export default function InvoiceList() {
  const { id } = useParams();
  const fy = useMemo(getCurrentFYRange, []);
  const [from, setFrom] = useState(fy.from);
  const [to, setTo] = useState(fy.to);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const money = (n) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const fmtDate = (dt) => {
    if (!dt) return "-";
    try {
      return new Date(dt).toLocaleDateString("en-GB"); // dd/mm/yyyy
    } catch {
      return "-";
    }
  };

  async function fetchInvoices(signal) {
    setLoading(true);
    setErr("");
    try {
      const token = localStorage.getItem("token");
      // add from/to
      const url = `${API_BASE_URL}/api/invoices/customer/${id}?from=${from}&to=${to}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      const rows = Array.isArray(res.data) ? res.data : res.data?.rows || [];
      setInvoices(rows);
    } catch (e) {
      if (axios.isCancel && axios.isCancel(e)) return;
      console.error("Failed to load invoices:", e);
      setErr("Failed to load invoices. Please try again.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }

  // load on mount and whenever customer/date range changes
  useEffect(() => {
    const controller = new AbortController();
    fetchInvoices(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, from, to]);

  const applyFY = () => {
    setFrom(fy.from);
    setTo(fy.to);
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header + count */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
            Invoices
          </h2>
          <div className="text-sm text-slate-600">
            Total: <span className="font-medium">{invoices.length}</span>
          </div>
        </div>

        {/* Date range filter (default = current FY) */}
        <div className="bg-white rounded-xl shadow p-3 flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="flex items-end gap-2">
            {/* Changing inputs auto-fetches; button provided for UX parity with other tabs */}
            <button
              onClick={() => fetchInvoices()}
              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
            >
              Apply
            </button>
            <button
              onClick={applyFY}
              className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200"
              title={`Reset to FY ${fy.from} → ${fy.to}`}
            >
              Current FY
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {err}
        </div>
      )}

      {/* ===== MOBILE: cards ===== */}
      <div className="sm:hidden space-y-3">
        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-500">
            Loading…
          </div>
        )}

        {!loading && invoices.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-500">
            No invoices found for this customer.
          </div>
        )}

        {!loading &&
          invoices.map((inv) => (
            <div
              key={inv.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-slate-900">
                    Invoice #{inv.id}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      {inv.customer_name || "—"}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                      Order: {inv.order_id ?? "—"}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      {fmtDate(inv.invoice_date)}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-sm font-semibold text-emerald-700">
                    ₹ {money(inv.total_amount)}
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <Link
                  to={`/invoices/${inv.id}`}
                  className="inline-flex w-full items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  View
                </Link>
              </div>
            </div>
          ))}
      </div>

      {/* ===== DESKTOP/TABLET: table ===== */}
      <div className="hidden sm:block overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="px-4 py-3 font-medium">#</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Order ID</th>
              <th className="px-4 py-3 font-medium text-right">Amount</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}

            {!loading &&
              invoices.map((inv) => (
                <tr key={inv.id} className="border-t hover:bg-slate-50 transition">
                  <td className="px-4 py-3">Invoice #{inv.id}</td>
                  <td className="px-4 py-3">{inv.customer_name}</td>
                  <td className="px-4 py-3">{inv.order_id}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold">
                    ₹ {money(inv.total_amount)}
                  </td>
                  <td className="px-4 py-3">{fmtDate(inv.invoice_date)}</td>
                  <td className="px-4 py-3">
                    <Link to={`/invoices/${inv.id}`} className="text-blue-600 hover:underline">
                      View
                    </Link>
                  </td>
                </tr>
              ))}

            {!loading && invoices.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-slate-500 py-6">
                  No invoices found for this customer.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
